<template>
  <div
    class="main-menu menu-fixed menu-accordion menu-shadow"
    :class="[
      {
        expanded:
          !isVerticalMenuCollapsed ||
          (isVerticalMenuCollapsed && isMouseHovered),
      },
      skin === 'semi-dark' ? 'menu-dark' : 'menu-light',
    ]"
    @mouseenter="updateMouseHovered(true)"
    @mouseleave="updateMouseHovered(false)"
  >
    <!-- main menu header-->
    <div class="navbar-header expanded">
      <slot
        name="header"
        :toggleVerticalMenuActive="toggleVerticalMenuActive"
        :toggleCollapsed="toggleCollapsed"
        :collapseTogglerIcon="collapseTogglerIcon"
      >
        <ul class="nav navbar-nav flex-row">
          <!-- Logo & Text -->
          <li class="nav-item mr-auto">
            <RouterLink class="navbar-brand" to="/">
              <span class="brand-logo">
                <Logo />
              </span>
              <h2 class="brand-text">
                {{ appName }}
              </h2>
            </RouterLink>
          </li>

          <!-- Toggler Button -->
          <li class="nav-item nav-toggle">
            <b-link class="nav-link modern-nav-toggle">
              <feather-icon
                icon="XIcon"
                size="20"
                class="d-block d-xl-none"
                @click="toggleVerticalMenuActive"
              />
              <feather-icon
                :icon="collapseTogglerIconFeather"
                size="20"
                class="d-none d-xl-block collapse-toggle-icon"
                @click="toggleCollapsed"
              />
            </b-link>
          </li>
        </ul>
      </slot>
    </div>
    <!-- / main menu header-->

    <!-- Shadow -->
    <div :class="{ 'd-block': shallShadowBottom }" class="shadow-bottom" />

    <!-- main menu content-->
    <vue-perfect-scrollbar
      ref="scrollContainer"
      :settings="perfectScrollbarSettings"
      class="main-menu-content scroll-area"
      tagname="ul"
      @ps-scroll-y="
        (evt) => {
          shallShadowBottom = evt.srcElement.scrollTop > 0;
        }
      "
    >
      <vertical-nav-menu-items
        :items="menuItems"
        class="navigation navigation-main"
      />
    </vue-perfect-scrollbar>
    <!-- /main menu content-->
  </div>
</template>

<script>
import navMenuItems from "@/navigation/vertical";
import events from "@/resources/systems-modules-events/events.json";
import useAppConfig from "@core/app-config/useAppConfig";
import Logo from "@core/layouts/components/Logo.vue";
import { $themeConfig } from "@themeConfig";
import { BImg, BLink } from "bootstrap-vue";
import { computed, provide, ref } from "vue";
import VuePerfectScrollbar from "vue-perfect-scrollbar";
import { RouterLink } from "vue-router";
import VerticalNavMenuItems from "./components/vertical-nav-menu-items/VerticalNavMenuItems.vue";
import useVerticalNavMenu from "./useVerticalNavMenu";

export default {
  components: {
    VuePerfectScrollbar,
    VerticalNavMenuItems,
    RouterLink,
    BLink,
    BImg,
    Logo,
  },
  props: {
    isVerticalMenuActive: {
      type: Boolean,
      required: true,
    },
    toggleVerticalMenuActive: {
      type: Function,
      required: true,
    },
  },
  watch: {
    isMouseHovered: function (newIsMouseHovered) {
      if (this.isVerticalMenuCollapsed && !newIsMouseHovered)
        this.$refs.scrollContainer.$el.scrollTop = 0;
    },
  },
  computed: {
    menuItems() {
      let items = navMenuItems;
      const economicGruopIndex = navMenuItems.findIndex(
        (it) => it.route == "admin-panel-group-economic-list"
      );
      const gruopIndex = navMenuItems.findIndex(
        (it) => it.route == "admin-panel-group-list"
      );
      const groupTitle = this.$store.state.appConfig.params.GRUPO_EMPRESA_TITLE;
      const hasEconomicGroup =
        this.$store.state.appConfig.params.EMP_ASSOCIACAO_GRUPO_ECONOMICO ==
        "S";
      const resourceEconomicGroup = hasEconomicGroup
        ? events.Emp_Grupos_Economicos.id
        : "all";

      items[economicGruopIndex].resource = resourceEconomicGroup;
      items[gruopIndex].title = groupTitle;

      return items;
    },
  },
  setup(props) {
    const {
      isMouseHovered,
      isVerticalMenuCollapsed,
      collapseTogglerIcon,
      toggleCollapsed,
      updateMouseHovered,
    } = useVerticalNavMenu(props);

    const { skin } = useAppConfig();

    // Shadow bottom is UI specific and can be removed by user => It's not in `useVerticalNavMenu`
    const shallShadowBottom = ref(false);

    provide("isMouseHovered", isMouseHovered);

    const perfectScrollbarSettings = {
      maxScrollbarLength: 60,
      wheelPropagation: false,
    };

    const collapseTogglerIconFeather = computed(() =>
      collapseTogglerIcon.value === "unpinned" ? "CircleIcon" : "DiscIcon"
    );

    // App Name
    let { appName } = $themeConfig.app;

    const domain = localStorage.getItem("domain");

    appName = domain;

    return {
      perfectScrollbarSettings,
      isVerticalMenuCollapsed,
      collapseTogglerIcon,
      toggleCollapsed,
      isMouseHovered,
      updateMouseHovered,
      collapseTogglerIconFeather,

      // Shadow Bottom
      shallShadowBottom,

      // Skin
      skin,

      // App Name
      appName,
    };
  },
};
</script>

<style lang="scss">
@import "~@core/scss/base/core/menu/menu-types/vertical-menu.scss";
</style>
